import { ref, unref } from 'vue';
import { escapeSql } from '@rdrive/helper-sql';
import { getCurrentUserTeamIds, toSqlIdList } from '@/utils/userTeams';

// Filter registration ids. These key both the filter registry and a DuckDB temp
// view (`filter_view_<id>`) — renaming one orphans its view and loses the
// filter, so they are byte-identical to the originals (R11).
const TEAMS_FILTER_ID = 'assignedToYourTeamsCardFilterContext';
const ME_FILTER_ID = 'assignedToMeCardFilterContext';

/**
 * The four headline counts on the Project and Drawings pages, plus the two
 * "assigned to…" switch filters those cards register.
 *
 * @param {object} deps
 * @param {object|import('vue').Ref} deps.conn
 * @param {object|import('vue').Ref} deps.filterContext
 * @param {string|import('vue').Ref} deps.userId
 * @param {boolean|import('vue').Ref} deps.showOpenItemsOnly
 */
export function useItemTotals({ conn, filterContext, userId, showOpenItemsOnly }) {
  const totalItems = ref(0);
  const totalSubItems = ref(0);
  const totalAssignedToYourTeams = ref(0);
  const totalAssignedToYou = ref(0);

  // Switch state, exposed so the cards can v-model it
  const filterByMyTeams = ref(false);
  const filterByMe = ref(false);

  // Cache user's team IDs to avoid re-querying with lambda functions
  const cachedUserTeamIds = ref([]);

  const db = () => unref(conn);
  const ctx = () => unref(filterContext);
  const uid = () => unref(userId);

  const refresh = async () => {
    try {
      const myTeamIds = await getCurrentUserTeamIds(db(), uid());
      cachedUserTeamIds.value = myTeamIds;
      const teamIdsList = toSqlIdList(myTeamIds);

      // Combined counts query - all from filteredItems
      // Use precise JSON array contains check to avoid partial matches (e.g., userId "1" matching "10")
      const combinedQuery = `
        SELECT
          COUNT(DISTINCT id) AS TotalItems,
          COUNT(subItemId) AS TotalSubItems,
          COUNT(CASE WHEN assigneeTeamId IN (${teamIdsList}) THEN subItemId END) AS AssignedToMyTeams,
          COUNT(CASE WHEN assigneeUserIds LIKE '%"${escapeSql(uid())}"%' THEN subItemId END) AS AssignedToMe
        FROM filteredItems
      `;

      const result = await db().query(combinedQuery);
      const row = result.toArray()[0];

      if (row) {
        const jsonRow = row.toJSON();
        totalItems.value = Number(jsonRow.TotalItems);
        totalSubItems.value = Number(jsonRow.TotalSubItems);
        totalAssignedToYourTeams.value = Number(jsonRow.AssignedToMyTeams);
        totalAssignedToYou.value = Number(jsonRow.AssignedToMe);
      }

    } catch (error) {
      console.error("Error executing counts query:", error);
      totalItems.value = 0;
      totalSubItems.value = 0;
      totalAssignedToYourTeams.value = 0;
      totalAssignedToYou.value = 0;
    }
  };

  // Fallback used only when the cached team list is empty: re-parse team_users
  // directly rather than going through the memoised helper.
  const loadTeamIdsFallback = async () => {
    try {
      const allTeamsQuery = `SELECT Id, Users FROM team_users`;
      const allTeamsResult = await db().query(allTeamsQuery);
      const allTeams = allTeamsResult.toArray().map(row => row.toJSON());

      cachedUserTeamIds.value = allTeams
        .filter(team => {
          let users = team.Users;
          // Handle if Users is a string (JSON)
          if (typeof users === 'string') {
            try {
              users = JSON.parse(users);
            } catch {
              return false;
            }
          }
          // Ensure users is an array
          if (!Array.isArray(users)) {
            return false;
          }
          return users.some(u => u.Id === uid() && u.Current === true);
        })
        .map(team => team.Id);
    } catch (fetchError) {
      console.error('Error fetching team IDs:', fetchError);
    }
  };

  const setAssignedToMyTeams = async (selected) => {
    filterByMyTeams.value = selected;

    if (!selected) {
      // Remove filter when untoggled
      await ctx().removeFilter(TEAMS_FILTER_ID);
      return;
    }

    try {
      // If no cached IDs, try to fetch them now
      if (cachedUserTeamIds.value.length === 0) {
        await loadTeamIdsFallback();
      }

      if (cachedUserTeamIds.value.length > 0) {
        const teamIdsList = toSqlIdList(cachedUserTeamIds.value);
        // Respect showOpenItemsOnly by joining with status_templates
        let query;
        if (unref(showOpenItemsOnly)) {
          query = `
              SELECT i.SubItemId
              FROM items i
              JOIN status_templates st ON i.StatusId = st.Id
              WHERE i.AssigneeTeamId IN (${teamIdsList})
                AND st.IsTerminal = FALSE
            `;
        } else {
          query = `SELECT SubItemId FROM items WHERE AssigneeTeamId IN (${teamIdsList})`;
        }
        await ctx().updateFilterWithQuery(TEAMS_FILTER_ID, query);
      }
    } catch (error) {
      console.error('Error querying for team filter:', error);
    }
  };

  const setAssignedToMe = async (selected) => {
    filterByMe.value = selected;

    if (!selected) {
      // Remove filter when untoggled
      await ctx().removeFilter(ME_FILTER_ID);
      return;
    }

    // Respect showOpenItemsOnly by joining with status_templates
    let query;
    if (unref(showOpenItemsOnly)) {
      query = `
          SELECT i.SubItemId
          FROM items i
          JOIN status_templates st ON i.StatusId = st.Id
          WHERE i.AssigneeUserIds LIKE '%"${escapeSql(uid())}"%'
            AND st.IsTerminal = FALSE
        `;
    } else {
      query = `SELECT SubItemId FROM items WHERE AssigneeUserIds LIKE '%"${escapeSql(uid())}"%'`;
    }
    try {
      await ctx().updateFilterWithQuery(ME_FILTER_ID, query);
    } catch (error) {
      console.error('Error querying for user filter:', error);
    }
  };

  return {
    totalItems,
    totalSubItems,
    totalAssignedToYourTeams,
    totalAssignedToYou,
    filterByMyTeams,
    filterByMe,
    setAssignedToMyTeams,
    setAssignedToMe,
    refresh
  };
}
